import React, { useEffect, useCallback, useState, useRef } from "react";
import { X, Download, ZoomIn, ZoomOut, RotateCw, FileText, ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import "./FileViewerModal.scss";

const VIEWABLE_IMAGES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml"];
const VIEWABLE_PDF = ["application/pdf"];
const VIEWABLE_TEXT = ["text/plain"];

const isViewable = (type) =>
  VIEWABLE_IMAGES.some((t) => type?.includes(t)) ||
  VIEWABLE_PDF.some((t) => type?.includes(t)) ||
  VIEWABLE_TEXT.some((t) => type?.includes(t));

/**
 * FileViewerModal — In-app file viewer overlay.
 * Renders images, PDFs, and text files inline. Non-viewable files get a download prompt.
 *
 * Props:
 *   show      — visibility flag
 *   onHide    — close handler
 *   fileUrl   — URL of the file
 *   fileName  — display name
 *   fileType  — MIME type
 */
const FileViewerModal = ({ show, onHide, fileUrl, fileName, fileType }) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [textContent, setTextContent] = useState("");
  const [textLoading, setTextLoading] = useState(false);
  const [pdfPage, setPdfPage] = useState(1);
  const [showMore, setShowMore] = useState(false);
  const moreRef = useRef(null);
  const pdfIframeRef = useRef(null);

  const isImage = VIEWABLE_IMAGES.some((t) => fileType?.includes(t));
  const isPdf = VIEWABLE_PDF.some((t) => fileType?.includes(t));
  const isText = VIEWABLE_TEXT.some((t) => fileType?.includes(t));

  // Reset state when file changes
  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setTextContent("");
    setPdfPage(1);
    setShowMore(false);
  }, [fileUrl]);

  // Navigate PDF to page without full iframe reload (hash navigation)
  useEffect(() => {
    if (!pdfIframeRef.current || !fileUrl) return;
    const base = fileUrl.split("#")[0];
    pdfIframeRef.current.src = `${base}#toolbar=0&navpanes=0&page=${pdfPage}`;
  }, [pdfPage, fileUrl]);

  // Close more menu on outside click
  useEffect(() => {
    if (!showMore) return;
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setShowMore(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMore]);

  // Load text file content
  useEffect(() => {
    if (!show || !isText || !fileUrl) return;
    let cancelled = false;
    setTextLoading(true);
    fetch(fileUrl)
      .then((res) => res.text())
      .then((text) => {
        if (!cancelled) setTextContent(text);
      })
      .catch(() => {
        if (!cancelled) setTextContent("Failed to load file content.");
      })
      .finally(() => {
        if (!cancelled) setTextLoading(false);
      });
    return () => { cancelled = true; };
  }, [show, isText, fileUrl]);

  // Escape to close (close more menu first)
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        if (showMore) { setShowMore(false); return; }
        onHide();
      }
    },
    [onHide, showMore]
  );

  useEffect(() => {
    if (show) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [show, handleKeyDown]);

  if (!show || !fileUrl) return null;

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = fileName || "download";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="fv-overlay" onClick={onHide}>
      <div className="fv-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        {/* Header */}
        <div className="fv-modal__header">
          <span className="fv-modal__filename" title={fileName}>{fileName}</span>
          <div className="fv-modal__toolbar">
            {/* PDF page navigation */}
            {isPdf && (
              <>
                <button
                  className="fv-modal__tool-btn"
                  onClick={() => setPdfPage((p) => Math.max(1, p - 1))}
                  title="Previous page"
                  disabled={pdfPage <= 1}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="fv-modal__page-label">{pdfPage}</span>
                <button
                  className="fv-modal__tool-btn"
                  onClick={() => setPdfPage((p) => p + 1)}
                  title="Next page"
                >
                  <ChevronRight size={16} />
                </button>
                <span className="fv-modal__divider" aria-hidden="true" />
              </>
            )}

            {/* Zoom — images + PDFs */}
            {(isImage || isPdf) && (
              <>
                <button className="fv-modal__tool-btn" onClick={() => setZoom((z) => Math.min(z + 0.25, 3))} title="Zoom in">
                  <ZoomIn size={16} />
                </button>
                <button className="fv-modal__tool-btn" onClick={() => setZoom((z) => Math.max(z - 0.25, 0.25))} title="Zoom out">
                  <ZoomOut size={16} />
                </button>
              </>
            )}

            <button className="fv-modal__tool-btn" onClick={handleDownload} title="Download">
              <Download size={16} />
            </button>

            {/* More menu — Rotate (keeps toolbar compact on mobile) */}
            {(isImage || isPdf) && (
              <div className="fv-modal__more-wrap" ref={moreRef}>
                <button
                  className={`fv-modal__tool-btn${showMore ? " fv-modal__tool-btn--active" : ""}`}
                  onClick={() => setShowMore((v) => !v)}
                  title="More options"
                >
                  <MoreHorizontal size={16} />
                </button>
                {showMore && (
                  <div className="fv-modal__more-menu">
                    <button
                      className="fv-modal__more-item"
                      onClick={() => { setRotation((r) => (r + 90) % 360); setShowMore(false); }}
                    >
                      <RotateCw size={14} /> Rotate 90°
                    </button>
                  </div>
                )}
              </div>
            )}
            <button className="fv-modal__close-btn" onClick={onHide} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="fv-modal__body">
          {isImage && (
            <div className="fv-modal__image-wrap">
              <img
                src={fileUrl}
                alt={fileName}
                className="fv-modal__image"
                style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }}
                draggable={false}
              />
            </div>
          )}

          {isPdf && (
            <div
              className="fv-modal__pdf-wrap"
              style={{
                zoom: zoom,
                transform: rotation ? `rotate(${rotation}deg)` : undefined,
                transformOrigin: "center top",
              }}
            >
              <iframe
                ref={pdfIframeRef}
                src={`${fileUrl.split("#")[0]}#toolbar=0&navpanes=0`}
                title={fileName}
                className="fv-modal__pdf"
              />
            </div>
          )}

          {isText && (
            <div className="fv-modal__text-wrap">
              {textLoading ? (
                <div className="fv-modal__loading">Loading...</div>
              ) : (
                <pre className="fv-modal__text">{textContent}</pre>
              )}
            </div>
          )}

          {!isImage && !isPdf && !isText && (
            <div className="fv-modal__unsupported">
              <FileText size={48} />
              <p className="fv-modal__unsupported-name">{fileName}</p>
              <p className="fv-modal__unsupported-hint">
                This file type cannot be previewed. Click download to view it.
              </p>
              <button className="fv-modal__download-btn" onClick={handleDownload}>
                <Download size={16} /> Download File
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

FileViewerModal.isViewable = isViewable;

export default FileViewerModal;
